import { IS_WEB } from 'constants/platform'
import { useTheme } from 'contexts'
import React, { useMemo, useRef, useState } from 'react'
import { RefreshControl } from 'react-native'

import { EventPostCard } from 'partials/EventPostCard'

import { Box } from 'widgets/Box'
import { FlatList } from 'widgets/FlatList'
import { LoadingSpinner } from 'widgets/LoadingSpinner'

import { Event } from 'types/event'

export const EventList = ({
  events,
  loading,
  noContentMessage,
  headerComponent,
  fetchNextEvents,
  refreshing,
  onRefresh,
  setIsCommentLoading,
  isFetchingNextPage,
}: {
  events?: Event[]
  loading?: boolean
  noContentMessage?: string
  headerComponent?: React.ReactElement
  fetchNextEvents: () => void
  refreshing?: boolean
  onRefresh?: () => void
  setIsCommentLoading: (isCommentLoading: boolean) => void
  isFetchingNextPage?: boolean
}) => {
  const theme = useTheme()
  const [isMuted, setIsMuted] = useState(false)
  const [visibleItems, setVisibleItems] = useState([])
  const [shouldCallOnScrollEnd, setShouldCallOnScrollEnd] = useState(false)

  const memoizedVisibleItems = useMemo(() => visibleItems, [visibleItems])

  const onViewableItemsChanged = useRef(({ viewableItems }: any) => {
    const visibleItemKeys = viewableItems.map((item: any) => item.key)
    setVisibleItems(visibleItemKeys)
  })

  return (
    <>
      <FlatList<Event>
        viewabilityConfig={{ itemVisiblePercentThreshold: 100 }}
        onViewableItemsChanged={onViewableItemsChanged.current}
        refreshControl={
          onRefresh ? (
            <RefreshControl
              refreshing={refreshing ?? false}
              onRefresh={onRefresh}
              progressBackgroundColor={theme.colors.v2primary}
              tintColor={theme.colors.v2primary}
            />
          ) : undefined
        }
        data={events}
        loading={loading}
        ListHeaderComponent={headerComponent}
        renderItem={({ item: event, index }) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const isViewable = memoizedVisibleItems.includes(event.id)
          return (
            <EventPostCard
              eventListIndex={index}
              postEvent={event}
              isViewable={isViewable}
              isMuted={isMuted}
              setIsMuted={setIsMuted}
              setIsCommentLoading={setIsCommentLoading}
            />
          )
        }}
        keyExtractor={(event) => event.id}
        style={!IS_WEB && { paddingBottom: theme.spacing.xl * 4 }}
        testID='EventsList'
        backgroundColor='white'
        noContentProps={{
          marginTop: 100,
          message: noContentMessage,
        }}
        onEndReachedThreshold={0.5}
        onEndReached={() => setShouldCallOnScrollEnd(true)}
        onMomentumScrollEnd={() => {
          if (shouldCallOnScrollEnd) {
            setShouldCallOnScrollEnd(false)
            fetchNextEvents()
          }
        }}
        ListFooterComponent={
          isFetchingNextPage ? (
            <Box padding={{ bottom: 16 }}>
              <LoadingSpinner />
            </Box>
          ) : null
        }
      />
    </>
  )
}
